import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Zap, CheckCircle, XCircle, Clock, Receipt } from 'lucide-react';

const statusColor = (s) => {
  if (s === 'success' || s === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (s === 'failed' || s === 'error') return 'bg-red-100 text-red-700';
  if (s === 'pending' || s === 'processing') return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-600';
};

const statusIcon = (s) => {
  if (s === 'success' || s === 'completed') return <CheckCircle size={16} className="text-emerald-500" />;
  if (s === 'failed' || s === 'error') return <XCircle size={16} className="text-red-500" />;
  return <Clock size={16} className="text-amber-500" />;
};

export default function CustomerPPOB() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/customer/ppob/transactions')
      .then(r => setTransactions(r.data.transactions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" />
    </div>
  );

  return (
    <div data-testid="customer-ppob-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Riwayat PPOB</h1>
        <p className="text-sm text-slate-500 mt-1">Histori transaksi pulsa, token PLN, paket data, dan tagihan</p>
      </div>

      {transactions.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <Zap size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="font-medium text-slate-900 mb-1">Belum ada transaksi PPOB</h3>
            <p className="text-sm text-slate-500">Transaksi pulsa, token, dan tagihan Anda akan muncul di sini</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {transactions.map(txn => (
            <Card key={txn.id} className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                      <Zap size={20} className="text-violet-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{txn.product_name || txn.product_code}</p>
                      <p className="text-sm text-slate-500">{txn.customer_number} · {txn.category || txn.type}</p>
                      <p className="text-xs text-slate-400 mt-1 font-mono">{txn.ref_id || txn.id?.slice(0, 12)}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="font-bold text-slate-900">Rp {fmt(txn.price || txn.amount)}</p>
                    <Badge className={statusColor(txn.status)}>
                      <span className="flex items-center gap-1">
                        {statusIcon(txn.status)}
                        {txn.status}
                      </span>
                    </Badge>
                    <p className="text-xs text-slate-400">{new Date(txn.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    {txn.sn && (
                      <p className="text-xs text-emerald-600 font-mono">SN: {txn.sn}</p>
                    )}
                  </div>
                </div>
                {txn.message && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-500 flex items-center gap-2">
                      <Receipt size={12} /> {txn.message}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
